// Token của Admin Web. KHÔNG hardcode màu, bo góc, cỡ chữ trong component — import từ đây.
//
// 🔴 ĐỔI 2026-08-28 (ADR-009): Admin Web KHÔNG còn bám prototype nữa. File này trước đây khớp 1-1
// với `:root` của `design/ui-prototypes/reski-admin-web.html`; nay nó là nguồn hình thức của riêng
// Admin Web. Member App và Staff App vẫn bám 42 màn prototype như cũ.
//
// Nhận diện nay là HAI màu: xanh Reski (chính) + cam (nhấn). Màu cam thay hẳn `clay` cũ.
import type { ThemeConfig } from 'antd';

/** Bảng màu và kích thước của Admin Web. */
export const tokens = {
  color: {
    green: '#014D21',
    green600: '#036B2E',
    green50: '#E8F2EC',
    green100: '#CFE5D9',

    // ── Cam: màu nhấn thứ hai của nhận diện (PL chốt 2026-08-28) ────────────────────────
    //
    // ⚠️ HAI sắc, và dùng sai chỗ là rớt WCAG AA — đã đo, đừng đổi mà không đo lại:
    //
    //   orange   #D97757  3.12:1 trên trắng → CHỈ dùng làm NỀN, chip, viền, icon, chữ LỚN.
    //                     Đạt ngưỡng 3:1 cho thành phần giao diện, KHÔNG đạt 4.5:1 cho chữ thường.
    //                     🔴 Chữ trắng trên nền này chỉ 3.12:1 → KHÔNG dùng làm nền nút đặc.
    //   orange600 #C4522C 4.57:1 trên trắng → chữ cam trên nền trắng, link, và nền nút đặc
    //                     (chữ trắng trên nó đạt 4.57:1).
    //
    // Trên nền sidebar tối (#0B2A18) thì `orange` đạt 4.95:1 nên dùng thẳng được.
    orange: '#D97757',
    orange600: '#C4522C',
    orange50: '#FBF1EE',
    orange100: '#F7E1DA',
    canvas: '#F6F8FA',
    surface: '#FFFFFF',
    surface2: '#F1F5F9',
    ink: '#0F172A',
    ink2: '#475569',
    ink3: '#94A3B8',
    line: '#E2E8F0',
    line2: '#CBD5E1',
    ok: '#16A34A',
    ok50: '#DCFCE7',
    warn: '#D97706',
    warn50: '#FEF3C7',
    danger: '#DC2626',
    danger50: '#FEE2E2',
    info: '#0284C7',
    info50: '#E0F2FE',
  },
  /** Thanh điều hướng dùng nền xanh đậm riêng, không phải màu primary */
  sidebar: {
    bg: '#0B2A18',
    text: '#D7E5DC',
    textMuted: '#8FB3A0',
    groupLabel: '#6E9280',
    activeBg: '#036B2E',
    hoverBg: 'rgba(255,255,255,.07)',
    border: 'rgba(255,255,255,.1)',
    width: 236,
  },
  radius: {
    card: 12,
    container: 16,
    control: 9,
    pill: 999,
  },
  space: {
    /** Lưới 4pt: padding card 16, gap section 24 (09-design-system) */
    cardPadding: 16,
    sectionGap: 24,
  },
  shadow: {
    card: '0 1px 2px rgba(15,23,42,.06), 0 1px 3px rgba(15,23,42,.04)',
  },
  motion: {
    /** Chuyển cảnh 150–300ms, đường cong này (quy tắc 8, 09-design-system) */
    ease: 'cubic-bezier(.16,1,.3,1)',
    duration: 200,
  },
  layout: {
    topbarHeight: 60,
  },
} as const;

/** Màu hạng thẻ — seed mặc định, admin đổi được (09-design-system). Không dùng để suy ra logic. */
export const tierColors = {
  dong: '#92400E',
  bac: '#94A3B8',
  vang: '#D97706',
  kimCuong: '#10B981',
} as const;

/**
 * Màu bậc hạng theo MÃ (bronze/silver/gold/diamond), không theo tên hiển thị — tên hạng đổi
 * được qua Admin Web (marketing đặt lại), mã thì cố định (09-design-system, TierDefinitionBootstrapper).
 * Mã lạ hoặc chưa xếp hạng dùng màu trung tính `ink3`.
 */
export function tierColorFor(code: string | null | undefined): string {
  switch (code) {
    case 'diamond':
      return tierColors.kimCuong;
    case 'gold':
      return tierColors.vang;
    case 'silver':
      return tierColors.bac;
    case 'bronze':
      return tierColors.dong;
    default:
      return tokens.color.ink3;
  }
}

export const reskiTheme: ThemeConfig = {
  token: {
    colorPrimary: tokens.color.green,
    // Link dùng CAM, không dùng xanh dương mặc định của antd. Xanh dương là một màu THỨ BA
    // lạc khỏi nhận diện hai màu; đổi chỗ này là cách rẻ nhất để cam thật sự có mặt trong app.
    // Dùng `orange600` (4,57:1) chứ không `orange` (3,12:1) vì link là CHỮ THƯỜNG.
    colorLink: tokens.color.orange600,
    colorLinkHover: tokens.color.orange,
    colorInfo: tokens.color.info,
    colorSuccess: tokens.color.ok,
    colorWarning: tokens.color.warn,
    colorError: tokens.color.danger,
    colorTextBase: tokens.color.ink,
    colorBgLayout: tokens.color.canvas,
    colorBorder: tokens.color.line,
    colorBorderSecondary: tokens.color.line,
    borderRadius: tokens.radius.card,
    borderRadiusLG: tokens.radius.container,
    borderRadiusSM: tokens.radius.control,
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 13,
    motionDurationMid: `${tokens.motion.duration}ms`,
    motionEaseInOut: tokens.motion.ease,
  },
  components: {
    Layout: {
      bodyBg: tokens.color.canvas,
      headerBg: tokens.color.surface,
      headerHeight: tokens.layout.topbarHeight,
      siderBg: tokens.sidebar.bg,
    },
    Card: {
      paddingLG: tokens.space.cardPadding,
    },
    Table: {
      headerBg: tokens.color.surface2,
    },
    // Alert mặc định của antd chiếm nhiều chiều dọc: padding rộng + icon to khi có description.
    // Thu gọn ở một chỗ để mọi banner/hint trong Admin Web dễ nhìn, không nuốt diện tích trang.
    Alert: {
      defaultPadding: '6px 12px',
      withDescriptionPadding: '10px 14px',
      withDescriptionIconSize: 20,
    },
    // Vùng chạm tối thiểu 44pt (quy tắc 2, 09-design-system)
    Button: {
      controlHeight: 36,
      controlHeightLG: 44,
    },
    // Segmented mặc định của antd: ô chọn là thumb TRẮNG trên track xám nhạt — hai màu quá gần
    // nhau, cỡ small thì gần như không thấy đang chọn cái nào. Cho ô chọn thành nền XANH brand +
    // chữ trắng để nổi hẳn (chữ trắng trên #014D21 thừa tương phản WCAG); ô chưa chọn để mực mờ.
    Segmented: {
      itemSelectedBg: tokens.color.green,
      itemSelectedColor: '#FFFFFF',
      itemColor: tokens.color.ink2,
      itemHoverColor: tokens.color.green,
      trackBg: tokens.color.surface2,
      // Bo tròn pill cả track lẫn ô chọn để hai đầu gọn, ô xanh không thò góc vuông ra khỏi track.
      borderRadius: tokens.radius.pill,
      borderRadiusSM: tokens.radius.pill,
      trackPadding: 3,
    },
  },
};
